import * as React from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Banner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/contexts/auth-context";
import { formatDate, formatDateTime } from "@/lib/format-date";
import { useAdminUserDetail } from "@/hooks/queries/useAdminUserDetail";
import {
  useDisableAdminUserAccess,
  useEnablePaidAccount,
  useEraseAdminUser,
  useReactivateAdminUserAccess,
  useResetAdminUserPassword,
} from "@/hooks/queries/useAdminUserActions";
// Story 20.3 (AC13) — every deadline label is IMPORTED from the shared module and
// none is re-derived here. The state labels read `deadline_state`, the anchor foot
// reads nothing at all (DW-660 — it names the FIELD, not the event that produced
// it), and this screen reads neither to decide anything else. A local
// `detail.user_origin === "webapp_legacy" ? … : …` here would be a second copy of
// a cohort rule that drifts from the clinician's own Screen 15 the first time
// either is edited — and the whole point of FR86 is that the admin and the
// clinician are looking at the same deadline.
import {
  DEADLINE_ANCHOR_FOOT,
  DEADLINE_STATE_BADGE_VARIANT,
  DEADLINE_STATE_LABEL,
  deadlineAnchorDay,
  type ShownDeadlineState,
} from "@/lib/calibration-deadline";
import { useAdminOrgs } from "@/hooks/queries/useAdminOrgs";
import { useAttachClinicianToOrg } from "@/hooks/queries/useAttachClinicianToOrg";
import { useReversalPreflight } from "@/hooks/queries/useReversalPreflight";
import { useReverseAttachment } from "@/hooks/queries/useReverseAttachment";
import type {
  AdminUserDetail,
  AdminUserEntitlementBlock,
  AdminUserSubscriptionBlock,
  AttachClinicianOutcome,
  ReverseAttachmentData,
} from "@/hooks/queries/admin-users-types";
import { CalibrationReviewNote } from "@/routes/calibration/review-flag-copy";
import { toAdminUserError, type AdminUserBanner } from "@/routes/admin/admin-user-error";
import {
  AttachToOrganizationDialog,
  type AttachToOrganizationDialogProps,
} from "@/routes/admin/AttachToOrganizationDialog";
import {
  NO_SUBSCRIPTION_BACK_SENTENCE,
  ReverseAttachmentDialog,
} from "@/routes/admin/ReverseAttachmentDialog";
import {
  adminAccessStatePill,
  adminCalibrationStatusLine,
  adminLiveScoringPill,
  EMPTY_CELL,
  hasLiveSubscriptionRow,
  roleLabel,
  subscriptionPill,
} from "@/routes/admin/admin-users-derivations";
import { ChangePasswordDialog } from "@/routes/admin/ChangePasswordDialog";
import { ExtendCalibrationDeadlineControl } from "@/routes/admin/ExtendCalibrationDeadlineControl";
import { DeleteUserDialog } from "@/routes/admin/DeleteUserDialog";
import { EnablePaidAccountDialog } from "@/routes/admin/EnablePaidAccountDialog";
import { ERASURE_RETENTION_STATEMENTS } from "@/routes/admin/erasure-retention-copy";
import {
  DisableAccessDialog,
  ReactivateAccessDialog,
} from "@/routes/admin/DisableAccessDialog";
import type {
  AdminUserDetailRouterState,
  AdminUsersListRouterState,
} from "@/routes/admin/AdminUsersListScreen";

// Screen 21 — the platform-admin user detail (Story 11.8, FR53/FR55/FR56/FR57).
//
// ONE read feeds all three cards. `seam-86-admin-card-mount` closes here by an
// EQUIVALENT mount: the calibration block from `GET /admin/users/{id}` renders in a
// super_admin host for the first time, rather than by mounting `ReadinessStatusCard`
// (which is first-person, self-addressed, and disagrees with the shared admin
// derivation about the `already_certified` cohort).
//
// RENDER-WHAT-YOU-GET (the UI half of `seam-143-user-detail-role-scoping`): the two
// severities render IFF the payload carries them. A super_admin receives them; an
// org_admin does not, and the suppression is 11.3's, server-side. This component
// holds nothing hidden — there is no role branch around the numbers, because a
// number present in JSON and merely hidden in the page is a P0 failure.
//
// The severities are EXACT decimal STRINGS and are printed verbatim. Never
// `parseFloat` / `Number()` them: a float round-trip is how a calibration value
// silently changes on the way to the screen that exists to report it.
//
// Deliberately absent: Admin Notes (no backing field on the wire — R6) and the
// audit timeline (absent, not empty — R8; 11.7's audit console is that surface).
//
// NO ACTION IS ADMISSIBLE ON AN ERASED ACCOUNT. `access_state === "erased"` is a
// GDPR Art. 17 tombstone: no credential to reset, no access to disable, nothing
// to reactivate, and nothing left to erase. Every control below is withheld on
// that one value rather than each being taught the same exception separately,
// because a control added later would otherwise default to being offered.

const HEADING_ID = "admin-user-detail-heading";

function displayName(detail: AdminUserDetail): string {
  return [detail.first_name, detail.last_name].filter(Boolean).join(" ") || detail.email;
}

/**
 * `min-w-0` + `overflow-wrap: anywhere` on the VALUE, because the values here
 * include an email address — one unbroken token, with no space the wrap can use.
 * `flex-wrap` alone moves such a value onto its own line and then lets it run
 * past the card edge: a flex item defaults to `min-width: auto`, so it refuses to
 * shrink below its content, and the address is visually truncated mid-domain
 * (`…@example.cor`). An operator reading a support ticket cannot tell a clipped
 * address from a wrong one.
 *
 * `anywhere` rather than `break-all`: it breaks only when the token genuinely
 * cannot fit, so ordinary prose values ("Org admin", "admin_created") keep
 * breaking at spaces and are untouched.
 */
function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline gap-2">
      <span className="text-sm font-semibold text-[color:var(--text-primary)]">{label}</span>
      <span className="min-w-0 text-sm text-[color:var(--text-secondary)] [overflow-wrap:anywhere]">
        {value}
      </span>
    </div>
  );
}

/** Numeric fields carry `tabular-nums` so the two severities align under each other. */
function NumericField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-wrap items-baseline gap-2">
      <span className="text-sm font-semibold text-[color:var(--text-primary)]">{label}</span>
      <span className="text-sm text-[color:var(--text-secondary)] [font-variant-numeric:tabular-nums]">
        {value}
      </span>
    </div>
  );
}

function RefusalBanner({ banner, backTo }: { banner: AdminUserBanner; backTo: string }) {
  return (
    <Banner variant={banner.variant} title={banner.title}>
      <p>{banner.body}</p>
      {banner.code ? <p className="text-sm">{`Reference code: ${banner.code}`}</p> : null}
      {banner.backToUsers ? (
        <Button asChild variant="secondary" size="sm">
          <Link to={backTo}>Back to users</Link>
        </Button>
      ) : null}
    </Banner>
  );
}

export function AdminUserDetailScreen() {
  const { userId = "" } = useParams();
  const { user, hasRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // AC7's back-path. The list keeps every filter and the page number in its URL
  // search params, so "back to users" is only truthful if it carries them: an
  // admin who filtered to `?status=blocked&role=clinician&page=3`, opened a user
  // and pressed Back used to land on page 1 of an unfiltered list, losing the
  // position they were working through. The list hands its own search string
  // over in router state when it links here, which is the same mechanism the
  // erase confirmation already uses to travel the other way. Arriving without
  // it — a deep link, a bookmark, a fresh tab — degrades to the bare list.
  const listSearch = (location.state as AdminUserDetailRouterState | null)?.listSearch;
  const backToUsers = listSearch ? `/admin/users?${listSearch}` : "/admin/users";

  const { data: detail, isPending, error } = useAdminUserDetail(userId);

  const resetPassword = useResetAdminUserPassword(userId);
  const disableAccess = useDisableAdminUserAccess(userId);
  const reactivateAccess = useReactivateAdminUserAccess(userId);
  const enablePaidAccount = useEnablePaidAccount(userId);
  const attachClinician = useAttachClinicianToOrg(userId);
  const eraseUser = useEraseAdminUser(userId);

  const [confirming, setConfirming] = React.useState<
    | "reset"
    | "disable"
    | "reactivate"
    | "enable-paid"
    | "attach"
    | "reverse"
    | "delete"
    | null
  >(null);
  const [actionBanner, setActionBanner] = React.useState<AdminUserBanner | null>(null);
  const [resetSentTo, setResetSentTo] = React.useState<string | null>(null);
  // Story 16.7 AC15 — the enable-paid-account OUTCOME, which is not readable off
  // success alone. The endpoint answers a no-op with 200 and
  // `outcome: "already_active"`, so an admin who clicks a control the row's stale
  // data still offered would otherwise see the dialog close and conclude they
  // just activated somebody. The two outcomes get two different sentences.
  const [enableOutcome, setEnableOutcome] = React.useState<
    "activated" | "already_active" | null
  >(null);
  // Story 22.1 AC24 — the attach OUTCOME, for the same reason as above and with
  // one more value. All three are 200s, and the third
  // (`attached_cancellation_incomplete`) reports a seat that IS attached beside
  // a cancellation that did not finish: a boolean cannot carry it, and a plain
  // success banner would tell the administrator the billing is settled when a
  // card is still on file.
  const [attachOutcome, setAttachOutcome] = React.useState<AttachClinicianOutcome | null>(
    null,
  );
  // Story 22.3 — the reversal's own outcome. One shape and not three: this route
  // answers every refusal with a 409, so a 200 always means the same thing. The
  // BODY is kept rather than a boolean because the seat figures it returns are
  // the point of the action, and its success sentence has to repeat D1 — the
  // subscription does not come back — where an administrator will read it AFTER
  // the dialog that said so has closed.
  const [reverseOutcome, setReverseOutcome] =
    React.useState<ReverseAttachmentData | null>(null);

  // A refusal from the READ is the whole screen's state; a refusal from a WRITE is a
  // banner above a screen that still renders. Both go through the one mapper.
  const readBanner = error ? toAdminUserError(error).banner : null;

  // Every action clears the previous outcome first, so a success banner from one
  // action can never sit above the refusal of the next. Both callbacks close the
  // dialog: a refusal is reported on the screen, not inside a modal the admin has
  // to dismiss before they can read it.
  function runAction(mutate: (onError: (e: unknown) => void, onSuccess: () => void) => void) {
    setActionBanner(null);
    setResetSentTo(null);
    setEnableOutcome(null);
    setAttachOutcome(null);
    setReverseOutcome(null);
    mutate(
      (e) => {
        setConfirming(null);
        setActionBanner(toAdminUserError(e).banner);
      },
      () => setConfirming(null),
    );
  }

  if (readBanner) {
    return (
      <section className="mx-auto flex w-full max-w-[var(--width-xl)] flex-col gap-6">
        <h1 id={HEADING_ID} className="display-xs text-[color:var(--text-primary)]">
          User
        </h1>
        <RefusalBanner banner={readBanner} backTo={backToUsers} />
      </section>
    );
  }

  // Loading renders NOTHING — no skeleton primitive exists in components/ui.
  if (isPending || !detail) return null;

  const name = displayName(detail);
  const subscription = subscriptionPill(detail.subscription);
  const access = adminAccessStatePill(detail.access_state);
  // Derived ONLY from a key the payload actually carries — see the render below.
  const liveScoring = detail.live_scoring
    ? adminLiveScoringPill(detail.live_scoring)
    : null;
  // Story 22.2 (FR88). Two guards, and they answer different questions. The
  // OUTER one is authorization: the block is platform-oversight-only, so for an
  // org_admin the key is simply absent and no panel is derivable. The INNER one
  // is whether there is anything to report — an account with no unreachable row
  // and no recorded failure has a clean residual, and the panel's absence is
  // what says so. `unreachable` is optional on the wire only because the whole
  // card is served with `exclude_none`; treat a missing count as zero rather
  // than as unknown, which is what the server means by omitting a `0`.
  const residualBlock = detail.cancellation_residual;
  const residual =
    residualBlock &&
    ((residualBlock.unreachable ?? 0) > 0 ||
      residualBlock.last_cancellation_outcome === "failed")
      ? residualBlock
      : null;

  const isSelf = user?.userId === detail.user_id;
  // The BLOCK fact, read from its own field and never from the access pill.
  // `access_state` is a total rank, so `removed` (a seat the organization gave
  // back) covers `blocked` (an act of an administrator) whenever both are true —
  // and both being true is two clicks away: disable a seated clinician, then
  // remove their seat. Gating on the pill left that account with Reactivate
  // withheld, Disable offered on access that was already disabled, and Delete
  // greyed out under "disable access first". `is_blocked` is exactly the
  // precondition the server's own reactivate refusal keys on.
  const isBlocked = detail.is_blocked;
  // The Art. 17 tombstone. ONE guard for the whole toolbar (see the file header):
  // the server refuses every write on an erased target.
  //
  // It DOES coincide with `isBlocked` above, and always: erasure is only
  // reachable through a disable, so every tombstone carries a block stamp. That
  // is why erasure is checked FIRST in every gate below rather than relying on
  // the states being mutually exclusive — they never were, and reading the
  // exclusivity off the ranked enum is what let Reactivate be offered on a
  // tombstone back when `erased` still arrived here as `blocked`.
  const isErased = detail.access_state === "erased";

  // Boundary #17 — Disable is hidden for the acting admin's own account (the server
  // refuses it `409 self_target_refused`) and hidden while the target is already
  // blocked, so Disable and Reactivate are never offered together.
  const canDisable = !isErased && !isSelf && !isBlocked;
  // Boundary #18 — Reactivate is super_admin ONLY. 11.5's matrix denies org_admin,
  // and showing a control that 403s is the posture `nav-config.ts:52-57` refuses.
  // Do NOT widen a grant the backend has not made.
  //
  // `isBlocked` and NOT `access_state === "blocked"`: the same posture, applied
  // to the other axis. A seat removed with no block behind it still gets no
  // Reactivate — the server answers `409 ADMIN-014` there — while a blocked
  // account whose seat was also removed now gets one, because the server accepts
  // it. The gate follows the server's precondition instead of a label that
  // happens to agree with it in the common case.
  const canReactivate = !isErased && hasRole("super_admin") && isBlocked;
  // Story 11.12 AC10 — Delete is super_admin ONLY (AC1's MATRIX denies org_admin
  // outright) and hidden for the acting admin's own account, exactly as Disable
  // is: the server refuses a self-target with `409 self_target_refused`, and the
  // sibling control above already established that a permanently-refusing button
  // is not offered. Unlike Disable it is RENDERED-BUT-DISABLED for an account
  // whose access is still enabled, rather than hidden — that state is a
  // precondition the admin can clear in one click on this same screen, so the
  // control has to be visible enough to explain what to do first.
  // Hidden rather than rendered-disabled once the account IS erased: the
  // disabled state above says "not yet", and a second erasure is "never".
  const canDelete = !isErased && hasRole("super_admin") && !isSelf;
  const deleteBlockedByAccess = canDelete && !isBlocked;

  // Epic 20 close (review finding F4), completed by Story 22.6 (AC7/AC9) — the
  // SUBJECT half of FR86, on this screen.
  //
  // The deadline derivation used to be cohort-BLIND: it exempted only
  // `has_profile || already_certified`, so an org_admin or a second super_admin
  // whose account was older than the window arrived here carrying a real `held`.
  // Both write routes refuse such a target with `ADMIN-021`, and this screen has
  // to say the same thing — a card that prints "Deadline state: On hold" over a
  // platform administrator states a fact about them that no product rule can
  // produce.
  //
  // THE ANSWER IS THE SERVER'S. `calibration.deadline_subject` is derived from
  // the SAME RBAC matrix the two write routes ask
  // (`Capability.submit_calibration_case`, the single route a deadline gates),
  // so a cohort change is one MATRIX edit and this screen follows it. What stood
  // here before was `CALIBRATION_SUBJECT_ROLE: Role = "clinician"` — a seventh
  // surface restating the cohort, agreeing with the other six only by
  // coincidence of naming, with no CI gate able to catch the day it stopped
  // (DW-732, now closed). Do not reintroduce a role literal here: the frontend
  // cannot ask the matrix, which is precisely why the payload now carries its
  // answer.
  //
  // One term, used twice below, so the block and the controls cannot drift: a
  // card that draws the state while the toolbar withholds the remedy is the
  // shape that produced the finding.
  //
  // NOT a breach of this file's RENDER-WHAT-YOU-GET rule (see the header), and
  // the distinction is worth stating because the shapes look alike. That rule is
  // about WITHHELD data — a value the caller may not have, present in the JSON
  // and merely hidden in the page, which is a P0. Nothing is withheld here: the
  // server sends the calibration block for a non-subject too, and sends this
  // flag beside it precisely so the page can be told, rather than guess.
  //
  // AND THE BLOCK IS DELIBERATELY NOT WITHHELD — the note that used to end here
  // proposing that as "the cleaner fix … one layer down" in
  // `administration/user_details/query.py` is SUPERSEDED, not merely unbuilt
  // (DW-732's TRAP; 22.6 boundary row 4). Dropping the block for a non-subject
  // collapses "non-subject" into "`not_applicable` clinician", and a
  // `not_applicable` clinician is still offered Extend — so an org_admin would
  // be offered it too: a permanently refusing button, the posture
  // `nav-config.ts:52-57` refuses to take. Suppression and subjecthood are two
  // facts, so they travel as two fields and are read as two terms below.
  const targetIsCalibrationSubject = detail.calibration.deadline_subject;

  // `deadline_state` is required on the wire, but `not_applicable` is a real
  // value and means there is no deadline block to draw at all — not a missing
  // field. Narrowing it here is what lets the two label maps below be indexed
  // without a cast; they are `Record<ShownDeadlineState, …>` precisely so that a
  // fourth state added upstream fails the type-check instead of rendering
  // `undefined` into the badge.
  //
  // A non-subject narrows to `null` on the SAME line and not in a second branch
  // downstream: every deadline row on the card reads this one value, so there is
  // no row left that could render for an account the fence excludes.
  //
  // DECLARED BEFORE THE GATES (PR #821 review), because the Reactivate gate below
  // reads THIS value and never `detail.calibration.deadline_state` again. A gate
  // that re-derived the state from the payload would be a SECOND derivation of
  // the thing the card draws, and the card and the control would then be free to
  // disagree about the same clinician — a toolbar offering to lift a hold over a
  // card that says "On track", or withholding it over one that says "On hold".
  // One term, read twice, is the same rule `targetIsCalibrationSubject` above
  // states for the cohort.
  const calibrationDeadlineState: ShownDeadlineState | null =
    !targetIsCalibrationSubject ||
    detail.calibration.deadline_state === "not_applicable"
      ? null
      : detail.calibration.deadline_state;

  // Story 20.3 (AC11, boundary #18) — the two FR86 calibration-deadline actions,
  // gated on the SAME shape as Reactivate above: erasure first, then the role.
  //
  // EXTEND carries deliberately NO term on the calibration STATE. A clinician
  // whose calibration is settled has no deadline to move and the server says so
  // (`ADMIN-021`), which is a refusal this screen renders copy for by name
  // (`admin-user-error.ts`) — copy that would be unreachable if the control were
  // withheld in exactly the case that produces it. The server is the authority
  // here, as it is for `ADMIN-012` on Change Password two gates up. And Extend is
  // offered for a HELD clinician too: Screen 38 panel 4b says in as many words
  // that "Extend sets a new deadline and lifts the hold in one step", and the
  // server performs exactly that (`test_a_held_clinician_is_lifted_by_the_same_
  // write`), so hiding it for `held` would delete a documented operator path.
  //
  // The TARGET-ROLE term is a different kind of gate and is not a reversal of
  // that reasoning. A calibration state changes; a subject's membership of the
  // cohort does not, so this control would answer ADMIN-021 on that account
  // forever — which is exactly the permanently-refusing button
  // `nav-config.ts:52-57` refuses to offer. The ADMIN-021 copy stays reachable
  // on its other leg (a clinician the server derives as `not_applicable`).
  const canExtendCalibrationDeadline =
    !isErased && hasRole("super_admin") && targetIsCalibrationSubject;

  // REACTIVATE is ASYMMETRIC (PR #821 review), and the asymmetry is the finding.
  //
  // Both controls sat behind one gate with no `deadline_state` term, so
  // `admin.calibration_deadline.reactivated` — which FR86 treats as the record of
  // WHICH exception a platform administrator granted, on an append-only
  // seven-year store — was one mis-click away for a clinician who was never held.
  // The two audit event names are the only thing separating the two routes; a
  // console that offers both on every row makes that distinction meaningless.
  //
  // WHY NOT THE MIRROR ("Reactivate when held, Extend otherwise"): see the Extend
  // gate above. Extend lifting a hold is documented and implemented, so hiding it
  // for `held` deletes a path the screen is supposed to offer.
  //
  // WHY NOT A SERVER REFUSAL INSTEAD ("reactivate refuses unless held"): it would
  // make ADMIN-020 structurally unreachable on that route — `held` implies the
  // deadline in force is in the past, so `new == previous` always trips ADMIN-019
  // first — deleting the existing AC6 criterion `test_a_reactivation_that_changes
  // _nothing_is_refused_before_it_can_emit`. The server half of this invariant is
  // carried by ADMIN-022 instead, which refuses the shortening rather than the
  // caller.
  //
  // ADMIN-021 STAYS REACHABLE, which is a recorded night decision (20.3's
  // implementation artifact) narrowly about `not_applicable`: the triggers carry
  // no calibration-state gate. Under this asymmetric gate a `not_applicable`
  // clinician still gets EXTEND — the leg that produces the ADMIN-021 refusal the
  // copy exists for — so that decision is preserved exactly and no AC moves.
  const canReactivateCalibration =
    canExtendCalibrationDeadline && calibrationDeadlineState === "held";

  const anchorDay = deadlineAnchorDay(detail.calibration.deadline_anchor_at);

  // Story 16.7 AC15 — the manual rescue for an account whose payment landed but
  // whose activation event did not. Gated the way Reactivate is, and for the same
  // reason: a control that answers ADMIN-017 on nearly every account it could be
  // rendered on is a permanently-refusing button, and this screen does not offer
  // those. The four terms, each carrying its own refusal:
  //
  //   `invited`             — `derive_access_state`'s exact word for "the authz
  //                           row exists and was never activated", which IS the
  //                           population this action exists for. An `active`
  //                           target answers the no-op; a `removed` or `erased`
  //                           one is a seat/tombstone question this cannot touch.
  //   `!is_blocked`         — the server answers a blocked target `409 ADMIN-018`,
  //                           and a payment does not lift a block. Read from the
  //                           block FIELD, never off the ranked `access_state`,
  //                           for the reason `isBlocked` records above.
  //   live subscription row — mirrors the server's `has_completed_checkout`; see
  //                           `hasLiveSubscriptionRow`. Weaker than the server's
  //                           read on purpose, so this can offer a button the
  //                           server then refuses but never hide a legitimate one.
  //   super_admin           — the capability is `enable_paid_account`, which the
  //                           org_admin matrix does not carry.
  //
  // No `isSelf` term, deliberately: the acting admin is signed in, so they are
  // `active` and the `invited` term has already excluded them. Adding a guard for
  // a state the first term makes unreachable would read as though self-targeting
  // were otherwise possible here.
  const canEnablePaidAccount =
    !isErased &&
    hasRole("super_admin") &&
    !isBlocked &&
    detail.access_state === "invited" &&
    hasLiveSubscriptionRow(detail.subscription);

  // Story 22.1 AC21 (boundary row 5) — offered to a super_admin, on a target the
  // action can admit: a clinician who holds no seat.
  //
  // `!detail.org.organization_role_label` is the SEATLESS term and it is the one
  // that matters here. The server refuses an already-seated target `409 ORG-026`,
  // so leaving it out would put a permanently-refusing button on every rostered
  // clinician's page — the posture the Disable/Reactivate pair above already
  // settled.
  //
  // It is the LABEL, not `org_id`. Every account owns a personal organization
  // from registration (`authentication/register/handler.py` passes
  // `owner_for_personal_org=True`), and `organizations/contract.py`'s
  // `_ORG_CONTEXT_SQL` rank 3 returns that personal org for a seatless user —
  // the blanking carve-out in `administration/_shared/admin_user_rows.py` runs
  // only for a SCOPED caller, and a super_admin is unscoped. So `org_id` is
  // non-null for every real target on this screen and `!detail.org.org_id` could
  // never be true. `organization_role_label` is `NOT NULL` on `seat_assignments`
  // and `NULL` on both non-seat ranks, which makes it the actual discriminator.
  //
  // What this gate is NOT is an authorization boundary. Every term below has a
  // server refusal behind it (`ORG-009` not a clinician, `ORG-017` blocked,
  // `ORG-026` already seated, `ORG-027` erased), and the endpoint re-decides all
  // of them against state this page may be minutes behind. Hiding a control the
  // server would not refuse, or showing one it would, are both only convenience
  // errors; a gate that existed ONLY here would be the other thing.
  const canAttachToOrganization =
    !isErased &&
    hasRole("super_admin") &&
    !isBlocked &&
    detail.role === "clinician" &&
    !detail.org.organization_role_label;

  // Story 22.3 AC24 — the exact INVERSE of the seat term above, on the same
  // reasoning and with the same standing: convenience, never authorization.
  // A reversal needs a seat to reverse, so this is offered on a SEATED
  // clinician and the attach on a seatless one; the two controls are never
  // both live.
  //
  // Deliberately NOT gated on the correction window, on the provenance column
  // or on `isBlocked`. None of the three is on this payload — provenance and
  // `assigned_at` live behind the preflight GET the dialog opens with, and
  // blocking is orthogonal: a blocked account's seat is still the
  // organization's to get back. The dialog is where the window is answered, by
  // the server, and the endpoint re-decides all of it (`ORG-029`, `ORG-030`,
  // `ORG-031`, `ORG-027`).
  const canReverseAttachment =
    !isErased &&
    hasRole("super_admin") &&
    detail.role === "clinician" &&
    Boolean(detail.org.organization_role_label) &&
    Boolean(detail.org.org_id);

  return (
    <section className="mx-auto flex w-full max-w-[var(--width-xl)] flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h1 id={HEADING_ID} className="display-xs text-[color:var(--text-primary)]">
          {name}
        </h1>
        <p className="text-sm text-[color:var(--text-secondary)]">
          Account detail for operational support, subscription review, access control, and
          calibration tracking.
        </p>
      </div>

      {/* Stated at the top of the card, not only in the Access pill: it is the
          reason every control below is missing, and an admin who came here to
          act needs that answered before they go looking for the button. */}
      {isErased ? (
        <Banner variant="info" title="This account has been deleted">
          <p>
            The account holder&apos;s sign-in details and personal information were erased at
            their request. No further action can be taken on this account.
          </p>
          {/* The retentions render from ERASURE_RETENTION_STATEMENTS, never from a
              list written out here. This banner is the THIRD surface telling this
              story — the modal and the post-erase banner are the other two — and it
              was the one still naming two retentions after the other two named four.
              A fourth surface must import the same constant; the key-set test
              against the server's LAYERS_RETAINED is what makes a missed one fail. */}
          <ul className="mt-2 list-disc space-y-1 pl-5">
            {ERASURE_RETENTION_STATEMENTS.map(({ layer, sentence }) => (
              <li key={layer}>{sentence}</li>
            ))}
          </ul>
        </Banner>
      ) : null}

      {resetSentTo ? (
        // Names the ADDRESS, never the password — the credential does not travel
        // through this client at all.
        <Banner variant="success" title="Password reset sent">
          <p>{`A temporary password was emailed to ${resetSentTo}.`}</p>
        </Banner>
      ) : null}
      {/* AC15's two outcomes, said apart. `already_active` is a SUCCESS on the
          wire and an `info` here: nothing was wrong and nothing changed, and
          calling it "Account enabled" would credit the admin with an activation
          that had already happened — the one misreading the `outcome` field was
          added to the response to prevent. */}
      {enableOutcome === "activated" ? (
        <Banner variant="success" title="Account enabled">
          <p>{`${name} can sign in and begin calibration now.`}</p>
        </Banner>
      ) : null}
      {enableOutcome === "already_active" ? (
        <Banner variant="info" title="This account was already enabled">
          <p>
            Nothing changed — the activation had already gone through. Refresh to see the
            current state of this account.
          </p>
        </Banner>
      ) : null}
      {/* AC24's three outcomes, said apart — the same discipline as AC15's two
          above, with one more value and a sharper reason for it.

          All three are 200s and the seat is attached in all three; they differ
          only in what happened to the INDIVIDUAL billing, which is the half no
          administrator can see from here. `attached_cancellation_incomplete` is
          the one that must never be folded into a success: a card is still on
          file, and the only way it gets taken off is a person going to look. */}
      {attachOutcome === "attached_subscription_canceled" ? (
        <Banner variant="success" title="Attached, and the individual subscription cancelled">
          <p>
            {`${name} now holds an organization seat, and the individual subscription that paid for their access has been cancelled.`}
          </p>
        </Banner>
      ) : null}
      {attachOutcome === "attached_nothing_to_cancel" ? (
        <Banner variant="success" title="Attached">
          <p>
            {`${name} now holds an organization seat. There was no individual subscription to cancel.`}
          </p>
        </Banner>
      ) : null}
      {attachOutcome === "attached_cancellation_incomplete" ? (
        <Banner variant="warn" title="Attached — the subscription cancellation did not finish">
          <p>
            {`${name} holds the organization seat, but at least one individual subscription could not be cancelled and may still bill. Check this account in Stripe and cancel the subscription that failed — but only that one: if this attachment is later reversed, ${name} may buy individual access again, and that newer subscription is a legitimate purchase to leave alone.`}
          </p>
        </Banner>
      ) : null}
      {/* Story 22.3 AC26 — the reversal's single success, which still has to say
          the thing the dialog said, because the dialog is gone by the time this
          is read. The seat figure comes from the RESPONSE and is not recomputed
          from the detail payload, which this success has just invalidated. */}
      {reverseOutcome ? (
        <Banner variant="success" title="Attachment reversed">
          <p>
            {`${name} has been taken off the organization's roster, and the seat is back in its pool — ${reverseOutcome.remaining} now available.`}
          </p>
          <p className="mt-1">{NO_SUBSCRIPTION_BACK_SENTENCE}</p>
        </Banner>
      ) : null}
      {actionBanner ? <RefusalBanner banner={actionBanner} backTo={backToUsers} /> : null}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>Account</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            <Field label="Email:" value={detail.email} />
            <Field label="Role:" value={roleLabel(detail.role)} />
            <Field label="Created:" value={formatDate(detail.created_at)} />
            {/* Boundary #6 — origin and org context live HERE, not as a seventh
                column on the client-validated table. */}
            <Field label="Origin:" value={detail.user_origin ?? EMPTY_CELL} />
            <Field label="Organization:" value={detail.org.org_name ?? EMPTY_CELL} />
            {detail.org.organization_role_label ? (
              <Field label="Org role:" value={detail.org.organization_role_label} />
            ) : null}
            {detail.force_password_change ? (
              <Badge variant="pending">Must change password at next sign-in</Badge>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Access</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            <Field
              label="Access state:"
              value={<Badge variant={access.variant}>{access.label}</Badge>}
            />
            <Field
              label="Subscription:"
              value={<Badge variant={subscription.variant}>{subscription.label}</Badge>}
            />
            <NumericField
              label="Renewal:"
              value={
                detail.subscription.current_period_end
                  ? formatDate(detail.subscription.current_period_end)
                  : EMPTY_CELL
              }
            />
            {/* Story 22.2 (FR88) — what is still owed on this account's
                cancellation, and what a human has to do about it.

                Placed HERE, directly beneath Renewal, because it is a statement
                about this account's billing and belongs beside the two rows that
                already make one. Deliberately NOT gated on `access_state`: an
                erased account is exactly the population the reconciler exists
                for, and this screen withholds its whole action toolbar there —
                so a panel that hid itself on a tombstone would be invisible on
                every card that needs it and visible only where it reads clean.

                Rendered only when there IS a residual. `unreachable === 0` with
                no recorded failure is the normal state of almost every account,
                and a permanent "nothing to do" panel on the densest card in the
                console is noise an operator learns to skip past — which is how
                the one card that does need reading gets skipped too. The
                absence of the panel is the "clean" reading. */}
            {residual ? (
              <Banner
                variant="warn"
                title="Cancellation did not complete"
                className="mt-2"
                data-testid="cancellation-residual"
              >
                <NumericField label="Unreachable rows:" value={String(residual.unreachable ?? 0)} />
                {residual.last_cancellation_outcome ? (
                  <Field
                    label="Last attempt:"
                    value={residual.last_cancellation_outcome}
                  />
                ) : null}
                <Field
                  label="Stripe customer:"
                  value={residual.stripe_customer_id ?? EMPTY_CELL}
                />
                {/* The operator diagnosis, lifted from the reconciler's own
                    module docstring (`reconcile_subscription_cancellations/
                    command.py`). Three halves now, and the last two are the ones
                    that get forgotten: cancelling in the dashboard stops the
                    money but does NOT clear this count, because the lifecycle
                    handler resolves the local row by the id these rows do not
                    carry. An operator who is told only the first half comes back
                    to a card that still says the same thing and concludes the
                    cancellation failed again.

                    The middle one is WHICH subscription to cancel, and it is
                    the only one here that can cost somebody money. The handle
                    above is a CUSTOMER; one customer can hold both the stale
                    object this count names and a live one bought later. Epic 22
                    makes that shape reachable — a reversal hands the seat back
                    and does not restore the cancelled individual arrangement, so
                    individual access afterwards is a NEW purchase. The
                    instruction must therefore not say "cancel it" flatly, which
                    is what it said while that shape was unreachable.

                    The first half is conditioned on there BEING a handle. A
                    cancellable row with a NULL `stripe_customer_id` is the
                    common shape here — every seeded persona but two carries one
                    — and telling an operator to "take the Stripe customer id"
                    to a dashboard while the field directly above reads "—" is a
                    panel arguing with itself. The instruction changes; the
                    destination does not. */}
                <p className="text-sm text-[color:var(--text-secondary)]">
                  {residual.stripe_customer_id
                    ? "Take the Stripe customer id to the Stripe dashboard and check whether a live subscription is still there — no job can reach these rows."
                    : "No Stripe customer id is recorded against these rows, so there is no handle to look up — find the account in the Stripe dashboard by email and check whether a live subscription is still there. No job can reach these rows."}{" "}
                  Check this account's audit trail before cancelling anything: if an attachment
                  was reversed, this person may have bought individual access again, and the same
                  Stripe customer then holds both the stale subscription this count refers to and a
                  newer, legitimate one to leave alone. Cancelling in Stripe does not clear this
                  count: the local rows carry no subscription id, so the cancellation webhook
                  matches nothing. Clearing the count is a separate, deliberate correction by
                  whoever owns the database.
                </p>
              </Banner>
            ) : null}
            {/* The RESOLVED verdict — an override or a seat can outrank the newest
                billing row, which is why this and the Subscription pill can disagree.

                GUARDED, and it must stay guarded. `user_details/query.py` omits
                `live_scoring` when the resolved entitlement belongs to an
                organization this caller is fenced out of, so an absent key is a
                statement about the FENCE and never about the person. Rendering the
                pill unconditionally printed the fail-closed "Blocked" there — a
                false claim about a clinician who can score, indistinguishable from
                the genuine "Blocked" this same row shows for someone who cannot.
                The em-dash is the idiom this screen already uses for the other
                field the same fence withholds — the organization name, on the
                Account card — and it keeps the verdict vocabulary, the Badge, out
                of a row that has no verdict to give. */}
            <Field
              label="Live scoring:"
              value={
                liveScoring ? (
                  <Badge variant={liveScoring.variant}>{liveScoring.label}</Badge>
                ) : (
                  EMPTY_CELL
                )
              }
            />
            {/* Read-only entitlement (R2): no grant / edit / extend / revoke control
                ships in M5. Each key optional-chains — the endpoint is served with
                `response_model_exclude_none`, so an absent key is the normal case. */}
            {detail.entitlement.source ? (
              <Field label="Entitlement:" value={detail.entitlement.source} />
            ) : null}
            {detail.entitlement.valid_until ? (
              <NumericField
                label="Entitlement until:"
                value={formatDate(detail.entitlement.valid_until)}
              />
            ) : null}
            {detail.entitlement.granted_by ? (
              <Field label="Granted by:" value={detail.entitlement.granted_by} />
            ) : null}
            {detail.entitlement.notes ? (
              <Field label="Notes:" value={detail.entitlement.notes} />
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Calibration</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            <NumericField
              label="Status:"
              value={adminCalibrationStatusLine(detail.calibration)}
            />
            {/* VERBATIM decimal strings — see the file header. */}
            {detail.calibration.motor_severity ? (
              <NumericField
                label="Motor calibration value:"
                value={detail.calibration.motor_severity}
              />
            ) : null}
            {detail.calibration.process_severity ? (
              <NumericField
                label="Process calibration value:"
                value={detail.calibration.process_severity}
              />
            ) : null}
            <CalibrationReviewNote status={detail.calibration.calibration_review_status} />
            {detail.calibration.last_case_activity_at ? (
              <NumericField
                label="Last case activity:"
                value={formatDateTime(detail.calibration.last_case_activity_at)}
              />
            ) : null}
            {/* Story 20.3 (AC12/AC13) — the four FR86 deadline rows, ADDED to this
                card rather than replacing it. Panel 4b of Screen 38 draws a
                Calibration card carrying only these rows; transcribing it as the
                whole card would DELETE the two capability-gated severities above,
                which are `seam-143`'s entire subject (boundary row 12). The mockup
                shows the deadline block, not the card's full contents.

                Each row optional-chains on its own because the three DATES are
                withheld from a caller who is not numeric-authorized while
                `deadline_state` never is — an org_admin is entitled to know a
                clinician in their organization is on hold, and entitled to exactly
                that (`admin-users-types.ts`). */}
            {calibrationDeadlineState ? (
              <>
                {anchorDay ? (
                  <NumericField
                    label="Clock started:"
                    value={`${formatDate(anchorDay)} \u2014 ${DEADLINE_ANCHOR_FOOT}`}
                  />
                ) : null}
                {detail.calibration.calibration_deadline_at ? (
                  <NumericField
                    label="Calibration deadline:"
                    value={formatDate(detail.calibration.calibration_deadline_at)}
                  />
                ) : null}
                {/* A Badge, not a NumericField: the state is the one row on this
                    block an admin scans for, and it is the same badge vocabulary
                    the clinician sees on their own screen. */}
                <Field
                  label="Deadline state:"
                  value={
                    <Badge variant={DEADLINE_STATE_BADGE_VARIANT[calibrationDeadlineState]}>
                      {DEADLINE_STATE_LABEL[calibrationDeadlineState]}
                    </Badge>
                  }
                />
                {/* Rendered even when unset, unlike every optional row above it:
                    panel 4b draws `Extended to: —`, and it is the one row whose
                    ABSENCE is the fact — "no exception has been granted to this
                    clinician" is what an admin about to grant one needs to see.
                    Suppressed only for a caller who receives no dates at all. */}
                {detail.calibration.calibration_deadline_at ? (
                  <NumericField
                    label="Extended to:"
                    value={
                      detail.calibration.deadline_extended_to
                        ? formatDate(detail.calibration.deadline_extended_to)
                        : EMPTY_CELL
                    }
                  />
                ) : null}
              </>
            ) : null}
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col gap-3">
        <h2 className="text-lg font-semibold text-[color:var(--text-primary)]">Actions</h2>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <Button asChild variant="secondary">
              <Link to={backToUsers}>Back to users</Link>
            </Button>
            {/* Never gated on `user_origin`: a webapp_legacy + inactive target is a
                VALID target (seam-114). The learnworlds_new refusal arrives from the
                server as ADMIN-012 and is surfaced, not pre-empted. The ONE gate is
                erasure — there is no credential left to reset, and the reset would
                email a tombstone address at `erased.invalid`. */}
            {isErased ? null : (
              <Button variant="secondary" onClick={() => setConfirming("reset")}>
                Change Password
              </Button>
            )}
            {/* The two access-GRANTING controls, and they belong on this side.
                Screen 21's toolbar has exactly two groups — ordinary actions and
                danger — so a control that hands an account its access back reads
                as a mis-grouping in the danger cluster, quietly outlined next to
                a filled "Disable access". They stay adjacent to each other
                because they are the two ways an account regains access and an
                admin looking for one will look here for the other; they are
                never offered together (Reactivate requires `isBlocked`, Enable
                paid account requires `!isBlocked`). */}
            {canReactivate ? (
              <Button variant="secondary" onClick={() => setConfirming("reactivate")}>
                Reactivate
              </Button>
            ) : null}
            {/* AC15. */}
            {canEnablePaidAccount ? (
              <Button variant="secondary" onClick={() => setConfirming("enable-paid")}>
                Enable paid account
              </Button>
            ) : null}
            {/* Story 22.1 AC21 — this FIRST group, beside Enable paid account,
                where Screen 21's toolbar draws it, and deliberately NOT in the
                danger cluster below. The attach does end an individual
                entitlement, which is what the dialog's must-read block is for;
                but what it does to the ACCOUNT is put it on a roster, and a
                filled red button would tell the administrator they are about to
                destroy something. */}
            {canAttachToOrganization ? (
              <Button variant="secondary" onClick={() => setConfirming("attach")}>
                Attach to organization
              </Button>
            ) : null}
            {/* Story 22.3 AC24 — beside the attach and on the same reasoning,
                because it is the same act read backwards: the reversal takes a
                seat off a roster and gives it back to a pool. NOT a `btn danger`
                (D11) — nothing is destroyed, the account stays enabled, and the
                one thing that does not come back was already gone before this
                button existed. The two are never offered together: the attach
                needs a seatless clinician and this one needs a seated one. */}
            {canReverseAttachment ? (
              <Button variant="secondary" onClick={() => setConfirming("reverse")}>
                Reverse attachment
              </Button>
            ) : null}
          </div>
          {/* Story 20.3 (AC11) — the calibration group, its OWN group between the
              ordinary actions and the danger cluster.

              Not folded into the group above: those controls act on the ACCOUNT
              (its password, its access, its billing), and these two act on the
              clinician's competency clock, which is a different subject and has a
              different reversal. Not folded into the danger group either — an
              extension grants time, it does not take anything away — and Screen
              21's shipped `Disable access` / `Delete User` pair is left exactly as
              it stands (boundary row 12).

              Panel 4b of Screen 38 draws these as `btn soft`, which is this
              toolbar's `variant="secondary"`. */}
          {canExtendCalibrationDeadline ? (
            <div className="flex flex-wrap items-center gap-3">
              <ExtendCalibrationDeadlineControl
                userId={detail.user_id}
                mode="extend"
                currentDeadline={detail.calibration.calibration_deadline_at}
              />
              {/* Nested under Extend's gate and then under its OWN: Reactivate is
                  offered only for a `held` clinician, because its audit event is
                  the permanent record of which exception was granted and there is
                  no exception to grant on an account that was never held. See
                  `canReactivateCalibration` for why the mirror of this gate is
                  NOT applied to Extend. */}
              {canReactivateCalibration ? (
                <ExtendCalibrationDeadlineControl
                  userId={detail.user_id}
                  mode="reactivate"
                  currentDeadline={detail.calibration.calibration_deadline_at}
                />
              ) : null}
            </div>
          ) : null}
          {/* The danger group, and ONLY destructive controls — mirroring Screen
              21's second toolbar group. 11.12 filled the Delete slot 11.8 left
              empty — the endpoint now exists, so the control is mounted rather
              than withheld. */}
          {/* Left-aligned, never `sm:items-end`: once the toolbar wraps — which it
              does at the widths an admin actually uses — end-alignment leaves the
              danger buttons floating mid-row, out of line with the group above
              them and with the helper sentence they belong to. */}
          <div className="flex flex-col items-start gap-1">
            <div className="flex flex-wrap items-center gap-3">
              {canDisable ? (
                <Button variant="destructive" onClick={() => setConfirming("disable")}>
                  Disable access
                </Button>
              ) : null}
              {canDelete ? (
                <Button
                  variant="destructive"
                  disabled={deleteBlockedByAccess}
                  onClick={() => setConfirming("delete")}
                >
                  Delete User
                </Button>
              ) : null}
            </div>
            {/* The disabled button's reason, in the same place the eye lands after
                finding the control greyed out. Says what to do, not what is wrong:
                the server's own `account_not_disabled` refusal is one click away
                and this exists so the admin never has to read it. */}
            {deleteBlockedByAccess ? (
              <p className="text-sm text-[color:var(--text-secondary)]">
                Disable this account's access before you can delete it.
              </p>
            ) : null}
          </div>
        </div>
      </div>

      <ChangePasswordDialog
        open={confirming === "reset"}
        onOpenChange={(open) => setConfirming(open ? "reset" : null)}
        userName={name}
        email={detail.email}
        isPending={resetPassword.isPending}
        onConfirm={() =>
          runAction((onError) => {
            resetPassword.mutate(undefined, {
              onSuccess: (data) => {
                setConfirming(null);
                setResetSentTo(data.emailed_to);
              },
              onError,
            });
          })
        }
      />

      <DisableAccessDialog
        open={confirming === "disable"}
        onOpenChange={(open) => setConfirming(open ? "disable" : null)}
        userName={name}
        isPending={disableAccess.isPending}
        onConfirm={(reason) =>
          runAction((onError, onSuccess) => {
            disableAccess.mutate(reason ? { reason } : {}, { onSuccess, onError });
          })
        }
      />

      <ReactivateAccessDialog
        open={confirming === "reactivate"}
        onOpenChange={(open) => setConfirming(open ? "reactivate" : null)}
        userName={name}
        isPending={reactivateAccess.isPending}
        onConfirm={() =>
          runAction((onError, onSuccess) => {
            reactivateAccess.mutate(undefined, { onSuccess, onError });
          })
        }
      />

      <EnablePaidAccountDialog
        open={confirming === "enable-paid"}
        onOpenChange={(open) => setConfirming(open ? "enable-paid" : null)}
        userName={name}
        isPending={enablePaidAccount.isPending}
        onConfirm={() =>
          runAction((onError) => {
            enablePaidAccount.mutate(undefined, {
              // The outcome is read from the RESPONSE, not assumed from success —
              // both outcomes are 200s and only this field tells them apart.
              onSuccess: (data) => {
                setConfirming(null);
                setEnableOutcome(data.outcome);
              },
              onError,
            });
          })
        }
      />

      {/* Mounted only while it is open, which is what ties the organization
          directory read to the dialog rather than to the page — see
          `AttachDialogHost`. */}
      {confirming === "attach" ? (
        <AttachDialogHost
          userName={name}
          entitlement={detail.entitlement}
          subscription={detail.subscription}
          isPending={attachClinician.isPending}
          onOpenChange={(open) => setConfirming(open ? "attach" : null)}
          onConfirm={(values) =>
            runAction((onError) => {
              attachClinician.mutate(
                { ...values, user_id: detail.user_id },
                {
                  // Read from the RESPONSE. All three outcomes are 200s and only
                  // this field tells them apart.
                  onSuccess: (data) => {
                    setConfirming(null);
                    setAttachOutcome(data.outcome);
                  },
                  onError,
                },
              );
            })
          }
        />
      ) : null}

      {/* Story 22.3 — mounted only while open, on the same reasoning as the
          attach host above and one of its own: the preflight GET discloses an
          organization's seat position and this account's roster provenance, and
          there is no reason for every visitor to this screen to fetch it.
          `detail.org.org_id` is re-tested here rather than trusted from
          `canReverseAttachment` so the host's props stay non-null by
          construction. */}
      {confirming === "reverse" && detail.org.org_id ? (
        <ReverseDialogHost
          orgId={detail.org.org_id}
          userId={detail.user_id}
          userName={name}
          orgName={detail.org.org_name ?? "this organization"}
          onOpenChange={(open) => setConfirming(open ? "reverse" : null)}
          runAction={runAction}
          onReversed={(data) => {
            setConfirming(null);
            setReverseOutcome(data);
          }}
        />
      ) : null}

      <DeleteUserDialog
        open={confirming === "delete"}
        onOpenChange={(open) => setConfirming(open ? "delete" : null)}
        userName={name}
        isPending={eraseUser.isPending}
        onConfirm={() =>
          runAction((onError) => {
            eraseUser.mutate(undefined, {
              // The confirmation lives on the LIST, carried in router state, for
              // the same reason 11.9's invite does: a banner rendered here would
              // paint on a screen that is unmounting. `name` is read from the
              // pre-erasure payload and passed as a VALUE — the refetch this
              // mutation just triggered will return the tombstone, so there is
              // nothing left on the server to name the person by afterwards.
              onSuccess: () => {
                setConfirming(null);
                const state: AdminUsersListRouterState = { erasedName: name };
                // Back to the list the admin was working through, filters and
                // page intact — the same back-path the toolbar link takes.
                navigate(backToUsers, { state });
              },
              onError,
            });
          })
        }
      />
    </section>
  );
}

/**
 * The attach dialog plus the organization directory read that feeds its picker.
 *
 * The read lives HERE, in a component the screen mounts only while the dialog is
 * open, and not beside the screen's other hooks. `useAdminOrgs` has no `enabled`
 * switch, so a call at the screen's top level would fire `GET /admin/orgs` on
 * every visit to every user detail page — including every org_admin visit, which
 * that endpoint answers 403 — to serve one control used on a handful of them.
 * Tying the read's lifetime to the dialog is also why the dialog takes
 * `orgsLoading`: at open there is genuinely nothing to offer yet.
 *
 * `limit: 100` is the server's documented maximum (`le=25`… `le=100`,
 * `admin-orgs-types.ts`), asked for once rather than paged: this picker is a
 * flat searchable list and a second page would silently halve it. When the
 * platform holds more than 100 organizations the dialog says so in its helper
 * line and the operator searches — `orgsTotal` is what carries that.
 */
function AttachDialogHost({
  userName,
  entitlement,
  subscription,
  isPending,
  onOpenChange,
  onConfirm,
}: {
  userName: string;
  entitlement: AdminUserEntitlementBlock;
  subscription: AdminUserSubscriptionBlock;
  isPending: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: AttachToOrganizationDialogProps["onConfirm"];
}) {
  const { data, isPending: orgsLoading } = useAdminOrgs({ limit: 100 });

  return (
    <AttachToOrganizationDialog
      open
      onOpenChange={onOpenChange}
      userName={userName}
      orgs={data?.results ?? []}
      orgsLoading={orgsLoading}
      orgsTotal={data?.count}
      entitlement={entitlement}
      subscription={subscription}
      isPending={isPending}
      onConfirm={onConfirm}
    />
  );
}

/**
 * The reversal dialog plus the eligibility read it opens on (Story 22.3, AC25).
 *
 * BOTH hooks live here rather than beside the screen's other mutations, and for
 * two different reasons. The preflight is a disclosure — an organization's seat
 * position and this account's roster provenance — so its lifetime is tied to
 * the dialog that needs it and not to the page. The mutation is here because
 * its route is keyed on the ORGANIZATION, and `org_id` is nullable on the
 * detail payload: a top-level call would have to invent an empty-string org for
 * every seatless user this screen renders.
 *
 * Nothing about the window is decided in this component. `reversible`,
 * `window_closes_at` and `window_days` are read off the response and handed to
 * the dialog, which is what keeps the browser from re-deriving a rule the
 * server owns.
 */
function ReverseDialogHost({
  orgId,
  userId,
  userName,
  orgName,
  onOpenChange,
  runAction,
  onReversed,
}: {
  orgId: string;
  userId: string;
  userName: string;
  orgName: string;
  onOpenChange: (open: boolean) => void;
  runAction: (mutate: (onError: (e: unknown) => void) => void) => void;
  onReversed: (data: ReverseAttachmentData) => void;
}) {
  const preflight = useReversalPreflight(orgId, userId);
  const reverse = useReverseAttachment(orgId, userId);

  return (
    <ReverseAttachmentDialog
      open
      onOpenChange={onOpenChange}
      userName={userName}
      orgName={orgName}
      preflight={preflight.data}
      preflightLoading={preflight.isPending}
      isPending={reverse.isPending}
      onConfirm={(values) =>
        runAction((onError) => {
          reverse.mutate(values, { onSuccess: onReversed, onError });
        })
      }
    />
  );
}
